import React, { useState, useEffect, useContext, useMemo } from "react";

import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import debounce from "lodash/debounce";
import ErrorBanner from "./ui/ErrorBanner";
import config from "../config";

import { Link, useNavigate } from "react-router-dom";
import { fetchBalance, updateUserLanguage } from "../utils/api";
import { formatCurrency } from "../utils/global";
import LanguageContext from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { buildLanguageOptions, DEFAULT_LANGUAGE_OPTION, getLanguageStorageCode } from "../utils/languageOptions";
import { assetPath } from "../utils/assets";

const emptyLanguageList = [];

const Header = ({ compact = false }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const languageContext = useContext(LanguageContext);
    const languageList = languageContext?.languageList || emptyLanguageList;
    const { token, phoneNumber } = useAuth();

    const [balanceLoading, setBalanceLoading] = useState(false);
    const [balance, setBalance] = useState(0);

    const [selectedLanguage, setSelectedLanguage] = useState(localStorage.getItem("language") || DEFAULT_LANGUAGE_OPTION.code);
    const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);

    const [errorMessage, setErrorMessage] = useState("");

    const languages = useMemo(() => buildLanguageOptions(languageList), [languageList]);

    useEffect(() => {
        const loadData = async () => {
            if (!token) {
                setBalance(0);
                setBalanceLoading(false);
                return;
            }

            setBalanceLoading(true);
            setErrorMessage("");

            try {
                const [balanceResponse] = await Promise.all([
                    fetchBalance()
                ]);

                if (balanceResponse.success) {
                    setBalance(balanceResponse.data);
                } else {
                    setBalance(0);
                }
            } catch (error) {
                setErrorMessage(t("failedToFetchLink"));
            } finally {
                setBalanceLoading(false);
            }
        };

        const debouncedLoadData = debounce(() => {
            loadData();
        }, 1000, { leading: true, trailing: false });

        const handleBalanceRefresh = () => debouncedLoadData();

        debouncedLoadData();
        window.addEventListener("wallet:refresh", handleBalanceRefresh);

        return () => {
            window.removeEventListener("wallet:refresh", handleBalanceRefresh);
            debouncedLoadData.cancel();
        };
    }, [t, token]);

    useEffect(() => {
        if (!languages.length) return;

        const storedLanguage = localStorage.getItem("language");
        const storedLanguageMatch = storedLanguage
            ? languages.find((lang) => lang.code === storedLanguage)
            : null;

        if (storedLanguageMatch) {
            if (storedLanguageMatch.code !== selectedLanguage) {
                setSelectedLanguage(storedLanguageMatch.code);
            }
            if (i18n.language !== storedLanguageMatch.i18nCode) {
                i18n.changeLanguage(storedLanguageMatch.i18nCode);
            }
            return;
        }

        const storedLanguageId = localStorage.getItem("language_id");
        if (!storedLanguageId) return;

        const match = languages.find((lang) => String(lang.id) === String(storedLanguageId));
        if (match && match.code !== selectedLanguage) {
            setSelectedLanguage(match.code);
            localStorage.setItem("language", match.code);
            i18n.changeLanguage(match.i18nCode);
        }
    }, [languages, i18n, selectedLanguage]);

    useEffect(() => {
        const currentLang = getLanguageStorageCode(i18n.language);
        const storedLang = localStorage.getItem("language");
        if (storedLang && selectedLanguage !== storedLang) {
            setSelectedLanguage(storedLang);
        } else if (!storedLang && currentLang !== selectedLanguage) {
            setSelectedLanguage(currentLang);
        }
    }, [i18n.language, selectedLanguage]);

    useEffect(() => {
        if (errorMessage) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [errorMessage]);

    const handleLanguageSelect = async (code) => {
        const selectedLang = languages.find((lang) => lang.code === code);
        if (!selectedLang?.id) {
            return;
        }
        setErrorMessage("");
        setSelectedLanguage(code);
        localStorage.setItem("language", code);
        localStorage.setItem("language_id", selectedLang.id);
        i18n.changeLanguage(selectedLang.i18nCode);
        setIsLanguageDropdownOpen(false);

        if (!token) {
            return;
        }

        try {
            const response = await updateUserLanguage(selectedLang.id);
            if (!response.success) {
                setErrorMessage(t("failedToUpdateLanguage"));
            }
        } catch (error) {
            setErrorMessage(t("failedToUpdateLanguage"));
        }
    };

    const activeLanguage = languages.find((lang) => lang.code === selectedLanguage)
        || languages.find((lang) => lang.code === DEFAULT_LANGUAGE_OPTION.code)
        || DEFAULT_LANGUAGE_OPTION;

    return (
        <header>
            <div className="relative flex-between p-4 bg-secondary">
                <div className="text-3xl font-bold">
                    <Link to="/" className="cursor-pointer">
                        <img
                            src={assetPath("/icons/logo.png")}
                            className="w-[130px]"
                            alt="Logo"
                        />
                    </Link>
                </div>

                {token && phoneNumber && (
                    <div className="ml-auto mr-2 z-50">
                        <span className="block max-w-[150px] truncate rounded-full border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-white/75">
                            {phoneNumber}
                        </span>
                    </div>
                )}

                <div className={`${token && phoneNumber ? "mr-0" : "mr-2 ml-auto"} z-50`}>
                    <div
                        className={`
                            relative cursor-pointer flex-center transition-all duration-200 px-2 pt-2 pb-2 border-2 
                            ${isLanguageDropdownOpen
                                ? "z-20 bg-[#000000] border-border-blue rounded-t-full border-b-0"
                                : "z-0 rounded-full border-transparent"
                            }
                        `}
                        onClick={() => setIsLanguageDropdownOpen((prev) => !prev)}
                    >
                        <div className="flex-center gap-2">
                            <img
                                src={activeLanguage.iconSrc}
                                alt={activeLanguage.name}
                                className="size-xl rounded-full"
                            />
                        </div>
                    </div>

                    <div
                        className={`
                            absolute z-10
                            bg-[#000000BF] backdrop-blur-sm border-2 border-border-blue
                            w-[calc(100vw-2rem)] p-4 transform transition-all duration-300
                            ${isLanguageDropdownOpen
                                ? "opacity-100 visible scale-100 translate-y-0"
                                : "opacity-0 invisible scale-95 -translate-y-2 pointer-events-none"
                            }
                        `}
                        style={{ left: "1rem", marginTop: "-2px" }}
                    >
                        <div className="flex flex-wrap gap-3 justify-center">
                            {languages.map((lang) => (
                                <div
                                    key={lang.id ?? lang.apiCode}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleLanguageSelect(lang.code);
                                    }}
                                >
                                    <div
                                        className={`
                                            size-10 rounded-full flex-center
                                            ${selectedLanguage === lang.code
                                                ? "p-[2px] "
                                                : "border-2 border-transparent"
                                            }
                                        `}
                                        style={selectedLanguage === lang.code ? {
                                            background: "linear-gradient(124.12deg, #DCFFD5 6.47%, #47FF25 91.98%)"
                                        } : {}}
                                    >
                                        <div
                                            className={`
                                                size-full rounded-full flex-center
                                                ${selectedLanguage === lang.code
                                                    ? "bg-black p-[3px]"
                                                    : ""
                                                }
                                            `}
                                        >
                                            <div className="size-full rounded-full overflow-hidden">
                                                <img src={lang.iconSrc} alt={lang.name} className="size-full object-cover" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {isLanguageDropdownOpen && (
                        <div
                            className="fixed inset-0 z-[5] cursor-default"
                            onClick={() => setIsLanguageDropdownOpen(false)}
                        />
                    )}
                </div>
            </div>

            {!compact && token && (
                balanceLoading ? (
                    <div className="ml-auto mr-1 flex items-center justify-center">
                        <Skeleton
                            height={36}
                            width={100}
                            borderRadius="9999px"
                            baseColor="#d1d5db"
                            highlightColor="#f3f4f6"
                            containerClassName="flex leading-none"
                        />
                    </div>
                ) : (
                    <div className="flex-center bg-[#000B11] border-y border-[#00476F] py-2">
                        <button
                            className="container-equal-xs gap-2"
                            onClick={() => {
                                navigate("/wallet")
                            }}
                        >
                            <span className="text-white text-sm font-semibold">
                                {`${config.CURRENCY_ISO_CODE} ${formatCurrency(balance, config.CURRENCY_ISO_CODE)}`}
                            </span>
                        </button>
                    </div>
                )
            )}

            {!compact && <ErrorBanner message={errorMessage} />}

            {!compact && <div className="flex-center gap-5 p-4 bg-secondary">
                {token ? (
                    <>
                        <Link
                            to="/deposit"
                            className="btn-gradient container-sm rounded-full gap-2 w-[164px] lg:rounded-[1.4rem] lg:w-auto lg:min-w-[180px] lg:text-sm lg:font-black lg:text-slate-950 lg:shadow-[0_18px_36px_-24px_rgba(0,151,201,0.95)] lg:transition-transform lg:duration-300 lg:ease-[cubic-bezier(0.22,1,0.36,1)] lg:hover:-translate-y-0.5 lg:active:scale-[0.98]"
                        >
                            <img
                                src={assetPath("/icons/deposit.png")}
                                alt="Deposit"
                                className="size-sm w-6 lg:hidden"
                            />
                            <p className="font-medium text-sm lg:font-black">{t("Deposit")}</p>
                        </Link>
                        <Link
                            to="/withdraw"
                            className="btn-gradient container-sm rounded-full gap-2 w-[164px] lg:w-auto lg:min-w-[180px] lg:rounded-[1.4rem] lg:border lg:border-[#0C77B1]/70 lg:bg-[#04537F] lg:bg-none lg:text-sm lg:font-black lg:text-white lg:shadow-none lg:transition-transform lg:duration-300 lg:ease-[cubic-bezier(0.22,1,0.36,1)] lg:hover:-translate-y-0.5 lg:hover:bg-[#0270AE] lg:active:scale-[0.98]"
                        >
                            <img
                                src={assetPath("/icons/withdraw.png")}
                                alt="Withdraw"
                                className="size-sm w-6 lg:hidden"
                            />
                            <p className="font-medium text-sm lg:font-black">{t("Withdraw")}</p>
                        </Link>
                    </>
                ) : (
                    <>
                        <Link
                            to="/login"
                            className="rounded-full border border-white/25 px-6 py-3 text-center text-sm font-black text-white transition-colors duration-300 hover:bg-white/10 w-[164px]"
                        >
                            {t("login")}
                        </Link>
                        <Link
                            to="/register"
                            className="rounded-full bg-[#119BFF] px-6 py-3 text-center text-sm font-black text-white transition-colors duration-300 hover:bg-[#1da5ff] w-[164px]"
                        >
                            {t("register")}
                        </Link>
                    </>
                )}
            </div>}
        </header>
    );
};

export default Header;
